import './Integrations.scss'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { ScrollNavigation, ScrollNavigationSection } from '../../components/NavigationWithSideBar/ScrollNavigation'
import { IntegrationCard, IntegrationCardProps } from '../../components/Integrations/IntegrationCard/IntegrationCard'
import { getIntegrationCategoryDisplayName, getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../common/integrations'
import React, { useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { useIntegrations } from './useIntegrations'
import { ManagedAtEnterpriseLevelInfo } from '../../components/ManagedAtEnterpriseLevel/ManagedAtEnterpriseLevelInfo'

interface IntegrationSectionConfig {
  id: string
  label: string
  title?: string
  integrations: Array<IntegrationCardProps>
}

const createIntegrationSection = ({ id, label, title, integrations }: IntegrationSectionConfig): ScrollNavigationSection => {
  return {
    id,
    label,
    title,
    content: (
      <Container fluid className='px-0 pb-5'>
        <Row className='g-3'>
          {integrations.map((integration) => (
            <Col key={integration.serviceId} xs={12} sm={6} xxl={4} className='d-flex justify-content-center integration-settings__common-cards'>
              <IntegrationCard serviceId={integration.serviceId} />
            </Col>
          ))}
        </Row>
      </Container>
    ),
  }
}

export const IntegrationDirectory: React.FC = () => {
  useDocumentTitle('Integrations')

  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })

  const { isIntegrationAllowed, isIntegrationCategoryAllowed, isLoading } = useIntegrations()

  const integrationCards: IntegrationCardProps[] = useMemo(() => {
    return Object.values(IntegrationServiceId)
      .filter((serviceId) => isIntegrationAllowed(serviceId))
      .map((serviceId) => ({ serviceId }))
  }, [isIntegrationAllowed])

  const getIntegrationsByCategory = useMemo(
    () =>
      (category: IntegrationCategory): IntegrationCardProps[] => {
        return integrationCards.filter((ic) => getIntegrationServiceIdsForCategory(category).includes(ic.serviceId as IntegrationServiceId))
      },
    [integrationCards],
  )

  const sections: ScrollNavigationSection[] = useMemo(() => {
    return Object.values(IntegrationCategory)
      .filter((category) => isIntegrationCategoryAllowed(category))
      .map((category) =>
        createIntegrationSection({
          id: category,
          label: getIntegrationCategoryDisplayName(category),
          integrations: getIntegrationsByCategory(category),
        }),
      )
  }, [isIntegrationCategoryAllowed, getIntegrationsByCategory])

  const connectedIntegrations = useMemo(() => {
    return userData?.integrations?.map((i) => i.serviceId as IntegrationServiceId) || []
  }, [userData])

  const connectedIntegrationCards: IntegrationCardProps[] = useMemo(() => {
    return integrationCards.filter((ic) => connectedIntegrations.includes(ic.serviceId as IntegrationServiceId))
  }, [connectedIntegrations, integrationCards])

  const connectedSection = useMemo(() => {
    return createIntegrationSection({
      id: 'connected',
      label: 'Connected',
      integrations: connectedIntegrationCards,
    })
  }, [connectedIntegrationCards])

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (!integrationCards.length) {
    return <ManagedAtEnterpriseLevelInfo resourceName={'Integrations'} />
  }

  return (
    <>
      <ScrollNavigation
        sections={[connectedSection, ...sections]}
        defaultActiveSectionId='connected'
        sectionClassName='integration-settings__integrations-container'
      />
    </>
  )
}
